import { AudioPlayer } from '@components/AudioPlayer';
import Download from '@components/icons/Download.svg';
import TryAgainIcon from '@components/icons/Jobs.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { UploadObjectType } from 'app/types/enums/uploadObjectType';
import { audioTypes } from '@helpers/audioTypes';
import { sanitizeFileName } from '@helpers/sanitizeFileName';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { useAPI } from '@providers/useAPI';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import { RepositoriesService } from 'app/API';
import { client } from 'app/http';
import bytes from 'bytes';
import { useEffect, useState } from 'react';
import { Loader } from '../Loader';
import { getDownloadLink } from './helpers/getDownloadLink';
import AttentionIcon from '@components/icons/FileAttention.svg';
import DocumentIcon from '@components/icons/FileDocument.svg';
import MicrophoneIcon from '@components/icons/FileMicrophone.svg';
import { getPreviewFileWrapperClassName } from './helpers/getPreviewFileWrapperClassName';

const previewFileErrorMessageClassName =
  'text-[var(--color-job-status-error)] text-left py-[5px] pb-2.5 text-xs leading-[18px] font-normal';
import { TProps } from './types/TProps';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';

export function PreviewFile({
  fileWithMeta,
  draggable = false,
  objectType,
  allowPlayback,
  hideRemoveButton = false,
  hideDownloadButton = false,
  onRemoveFile,
}: TProps) {
  const { call } = useAPI();
  const { form } = usePageContext();
  const { percent, status, size, Id, duration } = fileWithMeta.meta;
  const fileType = (fileWithMeta.meta.name || '').split('.').pop()?.toLowerCase();
  const isAudioFile = audioTypes.includes(`.${fileType}`);
  const isTrmFile = fileType === 'trm';
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  async function handleRemove() {
    if (!onRemoveFile) {
      return;
    }

    fileWithMeta.meta.status = 'removed';
    onRemoveFile(fileWithMeta);

    if (!Id) {
      console.error('No ID provided!', fileWithMeta, fileWithMeta.meta);
      return;
    }

    if (objectType === UploadObjectType.JOB) {
      client.delete('media', {
        headers: {
          'X-JobId': form.item?.id,
          'X-MediaId': fileWithMeta.meta.name,
          'X-Id': Id,
        },
      });
    }

    if (objectType === UploadObjectType.REPOSITORY) {
      await call(RepositoriesService.removeAttachment({ repositoryAttachmentId: Id }));
    }
  }

  useEffect(() => {
    if (
      !Id ||
      !allowPlayback ||
      !isAudioFile ||
      status !== 'done' ||
      objectType !== UploadObjectType.REPOSITORY ||
      isTrmFile
    ) {
      return;
    }
    const getPlaybackUrl = async () => {
      const data = await call(RepositoriesService.downloadAttachment({ repositoryAttachmentId: Id }));
      if (!data) {
        console.error('No data in response! Cannot set playback up.', data);
        return;
      }
      setPlaybackUrl(data.url);
    };

    getPlaybackUrl();
  }, [allowPlayback, isAudioFile, status, objectType, Id]);

  const fileName = (input: string) => {
    const name = sanitizeFileName(input);
    if (name?.length > 64) {
      return `${name.slice(0, 55)}...${name.slice(-6)}`;
    }
    return name;
  };

  const renderSecondLine = () => {
    if (status === 'done' && duration && duration !== Number.POSITIVE_INFINITY) {
      return (
        <span>
          Duration: {secondsToHHMMSS(duration ?? 0)} ({bytes(size)})
        </span>
      );
    }
    if (status === 'done' && duration === Number.POSITIVE_INFINITY) {
      return <span>Duration: Unrecognized ({bytes(size)})</span>;
    }
    if (status === 'done' && !duration) {
      return <span>File uploaded ({bytes(size)})</span>;
    }
    if (status === 'calculating_duration') {
      return <span>Analyzing...</span>;
    }
    if (status === 'pre_calculating_duration') {
      return <span>Analyzing...</span>;
    }
    if (status === 'rejected_file_type') {
      return <span></span>;
    }
    if (status === 'error_file_size') {
      return <span></span>;
    }
    return <span>Uploading: {`${Math.round(percent)}%`}</span>;
  };

  const renderIcon = () => {
    if (
      status === 'error_upload' ||
      status === 'error_analyzing' ||
      status === 'rejected_file_type' ||
      status === 'error_file_size'
    ) {
      return <AttentionIcon />;
    }
    if (status === 'done' && duration === 0) {
      return <AttentionIcon />;
    }
    if (status === 'done' && isAudioFile) {
      return <MicrophoneIcon />;
    }
    if (status !== 'done') {
      return <Loader size={20} />;
    }
    return <DocumentIcon />;
  };

  if (status === 'removed') {
    return null;
  }

  if (status === 'error_analyzing' || (status === 'done' && duration === 0)) {
    return (
      <div>
        <div className={getPreviewFileWrapperClassName(true, false)}>
          <div className="PreviewFile-row">
            <div className="PreviewFile-left-side-wrapper">
              <div className="PreviewFile-fileIcon">{renderIcon()}</div>
              <span className="PreviewFile-file-name-wrapper">
                <span>{fileName(fileWithMeta.meta.name)}</span>
                {renderSecondLine()}
              </span>
            </div>

            <div className="flex">
              {objectType === UploadObjectType.REPOSITORY && !hideDownloadButton ? (
                <button
                  className="PreviewFile-download-button"
                  data-testid="download-icon"
                  type="button"
                  onClick={() => getDownloadLink(Id, objectType, fileWithMeta)}
                >
                  <Download />
                </button>
              ) : null}

              {onRemoveFile && !hideRemoveButton ? (
                <IconButtonDelete
                  className="PreviewFile-button PreviewFile-removeButton"
                  type="button"
                  onClick={handleRemove}
                >
                  <TrashBin />
                </IconButtonDelete>
              ) : null}
            </div>
          </div>
        </div>

        <div className={previewFileErrorMessageClassName}>
          There was a problem analyzing your audio file. Please ensure the file will play in the audio player on your
          device. If playback is successful and the file is audible, please try uploading again. If the issue persists,
          please contact our{' '}
          <a
            href="https://support.voicescript.ai/portal/en/home"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            support team
          </a>{' '}
          for further assistance.
        </div>
      </div>
    );
  }

  return (
    <div className={getPreviewFileWrapperClassName(false, draggable && !allowPlayback)}>
      <div className="PreviewFile-row">
        <div className="PreviewFile-left-side-wrapper">
          <div className="PreviewFile-fileIcon">{renderIcon()}</div>
          <span className="PreviewFile-file-name-wrapper">
            <span>{fileName(fileWithMeta.meta.name)}</span>
            {renderSecondLine()}
          </span>
        </div>

        {status === 'rejected_file_type' ? <div className="errorText">File type unsupported</div> : null}
        {status === 'error_file_size' ? <div className="errorText">File is too big</div> : null}

        {status === 'error_upload' ? <div className="errorText">Error during uploading</div> : null}

        {status === 'error_upload' ? (
          <button className="PreviewFile-button PreviewFile-retryButton" type="button" onClick={fileWithMeta.upload}>
            <TryAgainIcon />
          </button>
        ) : null}

        <div className="flex">
          {objectType === UploadObjectType.REPOSITORY && status === 'done' && !hideDownloadButton ? (
            <button
              className="PreviewFile-download-button"
              data-testid="download-icon"
              type="button"
              onClick={() => getDownloadLink(Id, objectType, fileWithMeta)}
            >
              <Download />
            </button>
          ) : null}

          {onRemoveFile &&
          (status === 'done' ||
            status === 'error_upload' ||
            status === 'rejected_file_type' ||
            status === 'error_file_size') &&
          !hideRemoveButton ? (
            <IconButtonDelete
              className="PreviewFile-button PreviewFile-removeButton"
              type="button"
              onClick={handleRemove}
            >
              <TrashBin />
            </IconButtonDelete>
          ) : null}
        </div>
      </div>
      <div className="PreviewFile-row">
        {playbackUrl && !isTrmFile ? (
          <div className="PreviewFile-audio-wrapper">
            <AudioPlayer
              src={playbackUrl}
              downloadFileName={fileWithMeta.meta.name}
              onDownload={() => getDownloadLink(Id, objectType, fileWithMeta)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
